import { useState, useEffect } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import TestResults from "@/components/results/TestResults";
import Header from "@/components/layout/Header";
import Seo from "@/components/seo/Seo";

const categoriesMapping: Record<string, any[]> = {
  "math-3": [
    { id: "number-sense", name: "Number Sense and Numeration", questions: ["q1", "q5"] },
    { id: "measurement", name: "Measurement", questions: ["q3"] },
    { id: "geometry", name: "Geometry and Spatial Sense", questions: ["q2"] },
    { id: "patterning", name: "Patterning and Algebra", questions: ["q4"] },
  ],
  "reading-3": [
    { id: "comprehension", name: "Reading Comprehension", questions: ["q1"] },
    { id: "vocabulary", name: "Vocabulary", questions: ["q2"] },
    { id: "grammar", name: "Grammar", questions: ["q3"] },
  ],
  "math-6": [
    { id: "number-sense", name: "Number Sense and Numeration", questions: ["q1"] },
    { id: "measurement", name: "Measurement", questions: ["q2"] },
    { id: "geometry", name: "Geometry and Spatial Sense", questions: ["q3"] },
    { id: "patterning", name: "Patterning and Algebra", questions: ["q4"] },
    { id: "data", name: "Data Management and Probability", questions: ["q5"] },
  ],
  "reading-6": [
    { id: "comprehension", name: "Reading Comprehension", questions: ["q1"] },
    { id: "vocabulary", name: "Vocabulary and Word Analysis", questions: ["q2"] },
    { id: "analysis", name: "Critical Analysis", questions: ["q3"] },
  ],
  "writing-6": [
    { id: "purpose", name: "Purpose and Audience", questions: ["q1"] },
    { id: "organization", name: "Organization", questions: ["q2"] },
    { id: "conventions", name: "Language Conventions", questions: ["q3"] },
    { id: "voice", name: "Voice and Style", questions: ["q4"] },
  ],
  "writing-3": [
    { id: "purpose", name: "Purpose and Audience", questions: ["q1"] },
    { id: "organization", name: "Organization", questions: ["q2"] },
    { id: "conventions", name: "Language Conventions", questions: ["q3"] },
  ],
};

const ResultsPage = () => {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  
  const [loading, setLoading] = useState(true);
  const [testData, setTestData] = useState<any>(null);
  
  const stateData = location.state;
  
  useEffect(() => {
    if (stateData) {
      console.log("Using state data:", stateData); // Debug log
      setTestData(stateData);
      setLoading(false);
    } else {
      fetchTestResults();
    }
  }, [id, stateData]);
  
  const fetchTestResults = async () => {
    if (!id || id === 'demo' || !user) {
      setLoading(false);
      return;
    }
    
    try {
      const { data: sessionData, error: sessionError } = await supabase
        .from('test_sessions')
        .select(`
          id,
          grade,
          score,
          total_questions,
          time_taken,
          subjects(id, name)
        `)
        .eq('id', id)
        .eq('user_id', user.id)
        .single();
      
      if (sessionError || !sessionData) {
        console.error("Error fetching test session:", sessionError);
        setLoading(false);
        return;
      }
      
      const { data: answersData, error: answersError } = await supabase
        .from('test_answers')
        .select(`
          id,
          question_id,
          selected_option_id,
          is_correct,
          questions(
            id,
            text,
            explanation,
            category,
            category_id,
            options(id, text, is_correct)
          )
        `)
        .eq('test_session_id', id);
      
      if (answersError) {
        console.error("Error fetching test answers:", answersError);
        setLoading(false);
        return;
      }
      
      const { data: categoryData, error: categoryError } = await supabase
        .from('subjects_categories')
        .select('id, name')
        .eq('subject_id', sessionData.subjects.id);
        
      if (categoryError) {
        console.error("Error fetching categories:", categoryError);
      }
      
      const categoryMap = new Map();
      if (categoryData) {
        categoryData.forEach((cat: any) => {
          categoryMap.set(cat.id, cat.name);
        });
      }

      const questions = answersData.map(answer => {
        // Ensure we have valid data
        if (!answer.questions || !answer.questions.options) {
          console.error("Invalid question data:", answer);
          return null;
        }

        const categoryName = answer.questions.category_id && categoryMap.has(answer.questions.category_id) 
          ? categoryMap.get(answer.questions.category_id)
          : answer.questions.category;
          
        // Get the correct option ID by finding the option with is_correct=true
        const correctOption = answer.questions.options.find((opt: any) => opt.is_correct === true);
        const correctOptionId = correctOption ? correctOption.id : '';
        
        // Ensure options are properly formatted with string values
        const formattedOptions = answer.questions.options.map((opt: any) => ({
          id: opt.id,
          text: String(opt.text || ''),
          is_correct: Boolean(opt.is_correct)
        }));
          
        return {
          id: answer.questions.id,
          text: String(answer.questions.text || ''),
          explanation: answer.questions.explanation ? String(answer.questions.explanation) : undefined,
          options: formattedOptions,
          correctOptionId: correctOptionId,
          category: answer.questions.category,
          category_id: answer.questions.category_id,
          categoryName: categoryName ? String(categoryName) : undefined
        };
      }).filter(Boolean);
      
      const answers = answersData.reduce((acc: Record<string, string>, answer: any) => {
        if (answer.question_id && answer.selected_option_id) {
          acc[answer.question_id] = answer.selected_option_id;
        }
        return acc;
      }, {});
      
      let categories = [];
      
      if (categoryData && categoryData.length > 0) {
        categories = categoryData.map((cat: any) => {
          const categoryQuestions = questions.filter(q => q && q.category_id === cat.id).map(q => q?.id).filter(Boolean);
          
          return {
            id: cat.id,
            name: cat.name,
            questions: categoryQuestions
          };
        });
      } else {
        const subject = sessionData.subjects.name;
        const grade = sessionData.grade;
        const key = `${subject}-${grade}` as keyof typeof categoriesMapping;
        categories = categoriesMapping[key] || [];
      }
      
      // Log the data being set for debugging
      console.log("Setting test data:", {
        questions,
        answers,
        categories,
        grade: sessionData.grade,
        subject: sessionData.subjects.name,
        subjectId: sessionData.subjects.id
      });
      
      setTestData({
        questions,
        answers,
        categories,
        grade: sessionData.grade,
        subject: sessionData.subjects.name,
        subjectId: sessionData.subjects.id
      });
    } catch (error) {
      console.error("Error fetching test results:", error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleRetake = () => {
    window.history.back();
  };

  const handleContinuePractice = () => {
    if (testData && testData.grade) {
      navigate(`/grade/${testData.grade}`);
    } else {
      navigate('/dashboard');
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen flex flex-col bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50">
        <Header />
        <main className="flex-1 pt-20 flex items-center justify-center">
          <div className="text-center">
            <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto mb-4" />
            <p className="text-xl font-medium">Loading test results...</p>
          </div>
        </main>
      </div>
    );
  }
  
  if (!testData && id !== 'demo') {
    return (
      <div className="min-h-screen flex flex-col bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50">
        <Header />
        <main className="flex-1 pt-20 p-4">
          <div className="container mx-auto max-w-4xl">
            <div className="bg-destructive/10 text-destructive p-4 rounded-lg">
              <h2 className="text-xl font-bold">Test results not found</h2>
              <p>The test results you're looking for could not be found.</p>
            </div>
          </div>
        </main>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50">
      <Seo title="Test Results" description="Your Grade Test Pro test results." canonicalPath="/results" noindex />
      <Header />
      <main className="flex-1 pt-20 p-4">
        <div className="container mx-auto max-w-4xl">
          {testData && (
            <TestResults
              questions={testData.questions}
              answers={testData.answers}
              categories={testData.categories || []}
              grade={testData.grade}
              subject={testData.subject}
              categoryName={testData.categoryName}
              onRetake={handleRetake}
              onContinuePractice={handleContinuePractice}
            />
          )}
        </div>
      </main>
    </div>
  );
};

export default ResultsPage;
